/**
 * Meal Plan Details Page Component
 *
 * Displays detailed information about a specific meal plan, including
 * meal plan overview and individual meals. Based on the design from
 * docs/meal-plan-ui/app/meal-plan/[id]/page.tsx but adapted for the
 * BiteWise healthcare provider platform.
 *
 * @module components/pages/MealPlanDetailsPage
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Layout } from '@/components/layout/Layout';
import { useTheme } from '@/hooks/useTheme';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { useMealPlanDetails } from '@/hooks/useMealPlanDetails';
import { useMealPlanStore } from '@/stores/mealPlanStore';
import { useUserProfileStore } from '@/stores/userProfileStore';
import type { MealPlanFrontend, MealAction } from '@/types/mealPlan';
import { TitleText } from '@/components/ui/TitleText';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { BackButton } from '@/components/ui/BackButton';
import { AvatarPhoto } from '@/components/ui/AvatarPhoto';
import { Toast } from '@/components/ui/Toast';
import { MealDetailsDialog } from '@/components/meal-plans/MealDetailsDialog';
import { MealActionsMenu } from '@/components/meal-plans/MealActionsMenu';
import { EditMealDialog } from '@/components/meal-plans/EditMealDialog';
import { AdjustMealDialog } from '@/components/meal-plans/AdjustMealDialog';
import { SwapMealDialog } from '@/components/meal-plans/SwapMealDialog';
import { AddIngredientsModal } from '@/components/shopping-list/AddIngredientsModal';
import { useShoppingList } from '@/hooks/shopping-list/useShoppingList';
import { usePDFExport } from '@/hooks/usePDFExport';
import { getLoggerWithPrefix } from '@/services/shared/LoggerFactory';

/**
 * MealPlanDetailsPage Component Props
 */
export interface MealPlanDetailsPageProps {
  className?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  fullWidth?: boolean;
}

/**
 * Main Meal Plan Details Page Component
 *
 * Fetches and displays detailed information about a specific meal plan
 * using the meal plan ID from the URL parameters.
 */
export const MealPlanDetailsPage: React.FC<MealPlanDetailsPageProps> = ({
  className,
  showHeader = true,
  showFooter = true,
  fullWidth = false,
}) => {
  const logger = getLoggerWithPrefix('MealPlanDetailsPage');
  const { isDark } = useTheme();
  const { user } = useAuth();
  const profile = useUserProfileStore((state) => state.profile);
  const { id } = useParams<{ id: string }>();
  const { toast, showSuccess, showError, closeToast } = useToast();

  // Use custom hook for meal plan details
  const { mealPlan, loading, error, refresh } = useMealPlanDetails(id);
  const {
    exportMealPlan: exportMealPlanPdf,
    isGenerating: isPlanPdfGenerating,
  } = usePDFExport();
  const { exportMeal: exportMealPdf } = usePDFExport();
  const { shoppingList, addItems: addShoppingItems, mutating: isShoppingListMutating } = useShoppingList();

  // Get store actions
  const resetLoadingStates = useMealPlanStore((state) => state.resetLoadingStates);

  // State for dialogs
  const [dialogMeal, setDialogMeal] = useState<{ meal: MealPlanFrontend['mealPlanData']['meals'][0]; index: number } | null>(null);
  const [editDialogMeal, setEditDialogMeal] = useState<MealPlanFrontend['mealPlanData']['meals'][0] | null>(null);
  const [adjustDialogMeal, setAdjustDialogMeal] = useState<{ meal: MealPlanFrontend['mealPlanData']['meals'][0]; index: number } | null>(null);
  const [swapDialogMeal, setSwapDialogMeal] = useState<{ meal: MealPlanFrontend['mealPlanData']['meals'][0]; index: number } | null>(null);
  const [addIngredientsOpen, setAddIngredientsOpen] = useState(false);
  const [addingIngredients, setAddingIngredients] = useState(false);
  const [pendingIngredients, setPendingIngredients] = useState<{ name: string; quantity: number; unit: string }[]>([]);
  const [pendingMealCount, setPendingMealCount] = useState<number | null>(null);

  // Role checks
  const isPatient = profile?.role === 'patient';
  // Check if current user is the creator
  const currentUserId = profile?.id ?? user?.id;
  const isCreator = Boolean(mealPlan && currentUserId && mealPlan.creatorId === currentUserId);

  // Reset all dialog states
  const resetDialogStates = () => {
    setDialogMeal(null);
    setEditDialogMeal(null);
    setAdjustDialogMeal(null);
    setSwapDialogMeal(null);
  };

  // Handle meal action selection
  const handleMealAction = (meal: MealPlanFrontend['mealPlanData']['meals'][0], action: MealAction, index: number) => {
    switch (action) {
      case 'edit':
        setEditDialogMeal(meal);
        break;
      case 'adjust':
        setAdjustDialogMeal({ meal, index });
        break;
      case 'swap':
        setSwapDialogMeal({ meal, index });
        break;
    }
  };

  const buildPlanFilename = (plan: MealPlanFrontend): string => {
    const safeName = (plan.label || 'MealPlan').replace(/[^a-z0-9-]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    const dateSuffix = new Date().toISOString().split('T')[0];
    return `${safeName || 'MealPlan'}-${dateSuffix}.pdf`;
  };

  const mealsPerDay = useMemo(() => {
    if (!mealPlan?.creationParameters?.mealTypes?.length) {
      return 1;
    }
    return Math.max(1, mealPlan.creationParameters.mealTypes.length);
  }, [mealPlan]);

  const shoppingListItems = useMemo(() => {
    if (!mealPlan?.mealPlanData?.meals?.length) {
      return [];
    }

    return mealPlan.mealPlanData.meals.flatMap(meal =>
      (meal.ingredients ?? []).map(ingredient => ({
        name: ingredient.name,
        quantity: ingredient.amount ?? 0,
        unit: ingredient.unit || 'item',
      }))
    );
  }, [mealPlan]);

  const handleAddIngredients = async (mode: 'append' | 'replace') => {
    const itemsToAdd = pendingIngredients.length ? pendingIngredients : shoppingListItems;
    if (!itemsToAdd.length) {
      showError('No ingredients available to add.');
      setAddIngredientsOpen(false);
      return;
    }

    setAddingIngredients(true);
    try {
      await addShoppingItems({ mode, items: itemsToAdd });
      showSuccess('Ingredients added to shopping list');
      setAddIngredientsOpen(false);
      setPendingIngredients([]);
      setPendingMealCount(null);
    } catch (err) {
      console.error('Failed to add ingredients to shopping list:', err);
      showError('Unable to add ingredients right now. Please try again.');
    } finally {
      setAddingIngredients(false);
    }
  };

  const buildMealIngredients = (meal: MealPlanFrontend['mealPlanData']['meals'][0]) => {
    return (meal.ingredients ?? []).map(ingredient => ({
      name: ingredient.name,
      quantity: ingredient.amount ?? 0,
      unit: ingredient.unit || 'item',
    }));
  };

  const handleAddMealIngredients = async (meal: MealPlanFrontend['mealPlanData']['meals'][0]) => {
    if (!isPatient) {
      return;
    }
    const mealItems = buildMealIngredients(meal);
    if (!mealItems.length) {
      showError('No ingredients available for this meal.');
      return;
    }

    const existingCount = shoppingList?.items?.length ?? 0;
    if (existingCount > 0) {
      setPendingIngredients(mealItems);
      setPendingMealCount(1);
      setAddIngredientsOpen(true);
      return;
    }

    setAddingIngredients(true);
    try {
      await addShoppingItems({ mode: 'append', items: mealItems });
      showSuccess('Meal ingredients added to shopping list');
    } catch (err) {
      console.error('Failed to add meal ingredients:', err);
      showError('Unable to add meal ingredients right now. Please try again.');
    } finally {
      setAddingIngredients(false);
    }
  };

  const getDayMetadata = (mealIndex: number) => {
    if (!mealPlan) {
      return undefined;
    }
    const dayNumber = Math.floor(mealIndex / mealsPerDay) + 1;
    return {
      dayNumber,
      dayLabel: `Day ${dayNumber}`,
    };
  };

  const handleExportMealPlan = async () => {
    if (!mealPlan) {
      return;
    }

    try {
      await exportMealPlanPdf(mealPlan, buildPlanFilename(mealPlan));
      showSuccess('PDF export started. Check your downloads shortly.');
    } catch (exportError) {
      console.error('Failed to export meal plan PDF:', exportError);
      showError('Unable to export the PDF right now. Please try again.');
    }
  };

  const handleExportMeal = async (meal: MealPlanFrontend['mealPlanData']['meals'][0], mealIndex?: number) => {
    try {
      const metadata = typeof mealIndex === 'number' ? getDayMetadata(mealIndex) : undefined;
      const mealPayload =
        metadata !== undefined ? { ...meal, ...metadata } : meal;
      await exportMealPdf(mealPayload);
      showSuccess('Meal PDF export started. Check your downloads shortly.');
    } catch (exportError) {
      console.error('Failed to export meal PDF:', exportError);
      showError('Unable to export this meal PDF right now. Please try again.');
    }
  };

  // Don't show loading state immediately to prevent flicker
  const shouldShowLoading = loading && !mealPlan;

  // Set page title
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const title = mealPlan
        ? `${mealPlan.label || 'Meal Plan'} - BiteWise Healthcare Platform`
        : 'Meal Plan Details - BiteWise Healthcare Platform';
      document.title = title;
    }
  }, [mealPlan]);

  // Loading state - only show if we don't have data yet
  if (shouldShowLoading) {
    return (
      <Layout
        className={className}
        showHeader={showHeader}
        showFooter={showFooter}
        fullWidth={fullWidth}
        mainClassName=""
      >
        <div className="min-h-screen py-8 px-4">
          <div className="max-w-4xl mx-auto px-4">
            <div className="space-y-6">
              {/* Back Button Skeleton */}
              <div className="h-6 bg-neutral-300 rounded w-32 animate-pulse"></div>

              {/* Content Skeleton */}
              <Card className="p-8">
                <div className="animate-pulse space-y-4">
                  <div className="h-8 bg-neutral-300 rounded w-3/4"></div>
                  <div className="h-4 bg-neutral-300 rounded w-1/2"></div>
                  <div className="h-4 bg-neutral-300 rounded w-1/4"></div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout
        className={className}
        showHeader={showHeader}
        showFooter={showFooter}
        fullWidth={fullWidth}
        mainClassName=""
      >
        <div className="min-h-screen py-8 px-4">
          <div className="max-w-4xl mx-auto px-4">
            <div className="space-y-6">
              <div className="flex items-center gap-4 mb-4">
                <BackButton fallback='/meal-plans'/>
              </div>

              {/* Error Card */}
              <Card className="p-8">
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <TitleText className="text-red-600 mb-2">Error Loading Meal Plan</TitleText>
                    <BodyText variant="muted">{error}</BodyText>
                  </div>
                  <Button onClick={() => window.location.reload()} variant="primary">
                    Try Again
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <Layout
        className={className}
        showHeader={showHeader}
        showFooter={showFooter}
        fullWidth={fullWidth}
        mainClassName=""
      >
        <div className="min-h-screen py-8 px-4">
          <div className="max-w-4xl mx-auto px-4">
            <div className="space-y-6">
              <div className="flex items-center gap-4 mb-4">
                <BackButton fallback='/meal-plans'/>
              </div>

              {mealPlan && (
                <>
                  {/* Meal Plan Overview */}
                  <Card className="p-8">
                    <div className="space-y-6">
                      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div>
                          <TitleText className="mb-2">
                            {mealPlan.label || 'Untitled Meal Plan'}
                          </TitleText>
                          <div className="flex items-center gap-3">
                            <AvatarPhoto
                              src={mealPlan.creatorPhotoUrl}
                              firstName={mealPlan.creatorName?.split(' ')[0]}
                              lastName={mealPlan.creatorName?.split(' ').slice(1).join(' ')}
                              size="md"
                              alt={`${mealPlan.creatorName}'s profile photo`}
                            />
                            <BodyText variant="muted">
                              Created by {mealPlan.creatorName}
                            </BodyText>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            variant="primary"
                            loading={isPlanPdfGenerating}
                            onClick={handleExportMealPlan}
                            aria-label="Export meal plan to PDF"
                            className="inline-flex items-center gap-2 self-start"
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                              <polyline points="7 10 12 15 17 10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            {isPlanPdfGenerating ? 'Generating PDF...' : 'Export PDF'}
                          </Button>
                          {isPatient && (
                            <Button
                              variant="secondary"
                              onClick={() => {
                                const existingCount = shoppingList?.items?.length ?? 0;
                                if (existingCount > 0) {
                                  setPendingIngredients(shoppingListItems);
                                  setPendingMealCount(mealPlan?.mealPlanData?.meals?.length ?? null);
                                  setAddIngredientsOpen(true);
                                } else {
                                  void handleAddIngredients('append');
                                }
                              }}
                              disabled={isShoppingListMutating || !shoppingListItems.length}
                              loading={isShoppingListMutating}
                              aria-label="Add meal ingredients to shopping list"
                              className="inline-flex items-center gap-2 self-start"
                            >
                              <svg
                                className="w-4 h-4"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M3 5h18M7 10h10M5 15h14M10 20h4" />
                              </svg>
                              Add to Shopping List
                            </Button>
                          )}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <SubtitleText className="text-sm font-medium mb-1">Duration</SubtitleText>
                          <BodyText>{Math.ceil(mealPlan.mealCount / mealPlan.creationParameters.mealTypes.length)} days</BodyText>
                        </div>
                        <div>
                          <SubtitleText className="text-sm font-medium mb-1">Total Meals</SubtitleText>
                          <BodyText>{mealPlan.mealCount} meals</BodyText>
                        </div>
                        <div>
                          <SubtitleText className="text-sm font-medium mb-1">Estimated Cost</SubtitleText>
                          <BodyText>${mealPlan.totalEstimatedCostAud.toFixed(2)} AUD</BodyText>
                        </div>
                      </div>

                    </div>
                  </Card>

                  {/* Meals Section */}
                  <div className="space-y-6">
                    <SubtitleText>Meals</SubtitleText>
                    {mealPlan.mealPlanData && mealPlan.mealPlanData.meals && mealPlan.mealPlanData.meals.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {mealPlan.mealPlanData.meals.map((meal, index) => (
                          <Card
                            key={index}
                            className="p-6 cursor-pointer hover:shadow-lg transition-shadow duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                            onClick={() => {
                              logger.log('Meal card opened', { mealName: meal.mealName, index });
                              const metadata = getDayMetadata(index);
                              const contextualMeal = metadata ? { ...meal, ...metadata } : meal;
                              setDialogMeal({ meal: contextualMeal, index });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                logger.log('Meal card opened via keyboard', { mealName: meal.mealName, index, key: e.key });
                                const metadata = getDayMetadata(index);
                                const contextualMeal = metadata ? { ...meal, ...metadata } : meal;
                                setDialogMeal({ meal: contextualMeal, index });
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`View details for ${meal.mealName}`}
                          >
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <div className="flex justify-between items-center gap-2">
                                  <div className="flex justify-end gap-2 flex-1">
                                  {meal.cuisine && (
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      {meal.cuisine}
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    meal.mealType === 'breakfast' ? 'bg-yellow-100 text-yellow-800' :
                                    meal.mealType === 'morning_tea' ? 'bg-amber-100 text-amber-800' :
                                    meal.mealType === 'lunch' ? 'bg-green-100 text-green-800' :
                                    meal.mealType === 'afternoon_tea' ? 'bg-orange-100 text-orange-800' :
                                    meal.mealType === 'dinner' ? 'bg-blue-100 text-blue-800' :
                                    meal.mealType === 'dessert' ? 'bg-pink-100 text-pink-800' :
                                    'bg-purple-100 text-purple-800'
                                  }`}>
                                    {meal.mealType ? meal.mealType.charAt(0).toUpperCase() + meal.mealType.slice(1).replace('_', ' ') : 'Meal'}
                                  </span>
                                  </div>
                                  <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                                    <MealActionsMenu
                                      meal={meal}
                                      mealPlanId={mealPlan.id}
                                      onActionSelect={(action) => handleMealAction(meal, action, index)}
                                      position="card"
                                      onExportMeal={() => handleExportMeal(meal, index)}
                                      showEdit={isCreator}
                                      showAdjust={isCreator}
                                      showSwap={isCreator}
                                      showExport
                                      showAddToShoppingList={!isCreator && isPatient}
                                      onAddToShoppingList={() => handleAddMealIngredients(meal)}
                                    />
                                  </div>
                                </div>
                                <SubtitleText className="text-lg">{meal.mealName}</SubtitleText>
                              </div>

                              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                                <div className="flex items-center space-x-2 min-h-[24px]" title="Estimated cost" aria-label="Estimated cost">
                                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                    </svg>
                                  </div>
                                  <BodyText variant="muted" className="text-sm">{meal.estimatedCostAud.toFixed(2)} AUD</BodyText>
                                </div>
                                <div className="flex items-center space-x-2 min-h-[24px]" title="Number of servings" aria-label="Number of servings">
                                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                  </div>
                                  <BodyText variant="muted" className="text-sm">{meal.servings} servings</BodyText>
                                </div>
                                <div className="flex items-center space-x-2 min-h-[24px]" title="Preparation time" aria-label="Preparation time">
                                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  </div>
                                  <BodyText variant="muted" className="text-sm">{meal.prepTimeMinutes} min</BodyText>
                                </div>
                                <div className="flex items-center space-x-2 min-h-[24px]" title="Cooking time" aria-label="Cooking time">
                                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9h18M5 9l1 10h12l1-10M8 5h8" />
                                    </svg>
                                  </div>
                                  <BodyText variant="muted" className="text-sm">{meal.cookTimeMinutes ?? 0} min</BodyText>
                                </div>
                              </div>

                              {meal.nutrition && (
                                <div className="space-y-2">
                                  <BodyText className="font-medium mb-1">Nutrition (Per Serving)</BodyText>
                                  <div className="flex flex-col gap-2">
                                    <div className={`${isDark ? 'bg-secondary-800' : 'bg-gray-100'} rounded-lg px-3 py-2 text-center w-full`}>
                                      <BodyText className="text-sm font-medium">{meal.nutrition.caloriesPerServing}</BodyText>
                                      <BodyText variant="muted" className="text-xs">CALORIES</BodyText>
                                    </div>
                                    <div className="flex gap-2">
                                      <div className={`${isDark ? 'bg-secondary-800' : 'bg-gray-100'} rounded-lg px-3 py-2 text-center flex-1`}>
                                        <BodyText className="text-sm font-medium">{meal.nutrition.proteinG}g</BodyText>
                                        <BodyText variant="muted" className="text-xs">Protein</BodyText>
                                      </div>
                                      <div className={`${isDark ? 'bg-secondary-800' : 'bg-gray-100'} rounded-lg px-3 py-2 text-center flex-1`}>
                                        <BodyText className="text-sm font-medium">{meal.nutrition.carbsG}g</BodyText>
                                        <BodyText variant="muted" className="text-xs">Carbs</BodyText>
                                      </div>
                                      <div className={`${isDark ? 'bg-secondary-800' : 'bg-gray-100'} rounded-lg px-3 py-2 text-center flex-1`}>
                                        <BodyText className="text-sm font-medium">{meal.nutrition.fatG}g</BodyText>
                                        <BodyText variant="muted" className="text-xs">Fat</BodyText>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                            </div>
                          </Card>
                        ))}
                      </div>
                    ) : (
                      <Card className="p-8">
                        <BodyText variant="muted" className="text-center">
                          No meals available for this meal plan.
                        </BodyText>
                      </Card>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Layout>

      {/* Meal Details Dialog */}
      {dialogMeal && (
        <MealDetailsDialog
          isOpen={true}
          onClose={() => setDialogMeal(null)}
          meal={dialogMeal.meal}
          mealIndex={dialogMeal.index}
        />
      )}

      {/* Edit Meal Dialog */}
      {editDialogMeal && mealPlan && (
        <EditMealDialog
          isOpen={true}
          onClose={() => setEditDialogMeal(null)}
          meal={editDialogMeal}
          mealPlanId={mealPlan.id}
          onSuccess={async () => {
            showSuccess('Meal updated successfully');
            // Refresh meal plan data and reset all dialogs
            await refresh();
            resetDialogStates();
            resetLoadingStates();
          }}
        />
      )}

      {/* Adjust Meal Dialog */}
      {adjustDialogMeal && mealPlan && (
        <AdjustMealDialog
          isOpen={true}
          onClose={() => setAdjustDialogMeal(null)}
          meal={adjustDialogMeal.meal}
          mealPlanId={mealPlan.id}
          onSuccess={async () => {
            showSuccess('Meal adjusted successfully');
            // Refresh meal plan data and reset all dialogs
            await refresh();
            resetDialogStates();
            resetLoadingStates();
          }}
        />
      )}

      {/* Swap Meal Dialog */}
      {swapDialogMeal && mealPlan && (
        <SwapMealDialog
          isOpen={true}
          onClose={() => setSwapDialogMeal(null)}
          meal={swapDialogMeal.meal}
          mealPlanId={mealPlan.id}
          onSuccess={async () => {
            showSuccess('Meal swapped successfully');
            // Refresh meal plan data and reset all dialogs
            await refresh();
            resetDialogStates();
            resetLoadingStates();
          }}
          onError={(error) => {
            console.error('Failed to swap meal:', error);
          }}
        />
      )}

      {/* Add Ingredients Modal */}
      <AddIngredientsModal
        isOpen={addIngredientsOpen}
        onClose={() => {
          setAddIngredientsOpen(false);
          setPendingIngredients([]);
          setPendingMealCount(null);
        }}
        onConfirm={handleAddIngredients}
        mealCount={pendingMealCount ?? mealPlan?.mealPlanData?.meals?.length ?? 0}
        ingredientCount={(pendingIngredients.length || shoppingListItems.length)}
        isSubmitting={addingIngredients || isShoppingListMutating}
      />

      {/* Toast Notification */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={closeToast}
        duration={3000}
      />
    </>
  );
};
